/**
 * The address of a clip. One declared pattern - `/reel/{playlistId}/{mediaId}`, `?reel={mediaId}` -
 * read both ways: deep links, the address while scrolling, where closing goes, and the share link.
 * Without a pattern the player touches nothing. Reads window.location and no DOM.
 *
 * WARNING. None of this makes `borsen.dk/reel/PL/ME` survive a refresh; the SITE has to serve that
 * address. This only keeps the address honest once the page is running.
 */

const REELS_URL_TOKEN = /\{(mediaId|playlistId)\}/g;
// Stand-ins that survive the URL parser: a brace in a path would be escaped to %7B, and the
// substitution matches on braces. Swapped out first, swapped back at the end.
const REELS_TOKEN_MARKERS = { mediaId: 'qpz0mediaid0zqp', playlistId: 'qpz0playlistid0zqp' };

const reelsTemplate = (pattern) =>
	new URL(
		pattern.replace(REELS_URL_TOKEN, (match, token) => REELS_TOKEN_MARKERS[token] ?? match),
		window.location.href
	);

const reelsMatcher = (templatePart) => {
	const escaped = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const tokens = [];
	const source = templatePart
		.split(new RegExp(`(${REELS_TOKEN_MARKERS.mediaId}|${REELS_TOKEN_MARKERS.playlistId})`))
		.map((piece) => {
			const token = Object.keys(REELS_TOKEN_MARKERS).find((name) => REELS_TOKEN_MARKERS[name] === piece);
			if (!token) {
				return escaped(piece);
			}
			tokens.push(token);
			// A token is one segment: it stops at the separators a URL uses.
			return '([^/?&#]+)';
		})
		.join('');
	return { expression: new RegExp(`^${source}$`), tokens };
};

const fillReelsTokens = (text, values) =>
	text
		.split(REELS_TOKEN_MARKERS.mediaId)
		.join(values.mediaId ?? '')
		.split(REELS_TOKEN_MARKERS.playlistId)
		.join(values.playlistId ?? '');

/**
 * The clip the current address names, or null. Query parameters are matched one at a time, so a
 * campaign or consent parameter on a shared link does not stop the clip being recognised.
 */
export const readReelsUrl = (pattern) => {
	if (!pattern) {
		return null;
	}
	const template = reelsTemplate(pattern);
	const found = {};

	const path = reelsMatcher(template.pathname);
	const pathMatch = path.expression.exec(window.location.pathname);
	if (!pathMatch) {
		return null;
	}
	path.tokens.forEach((token, index) => {
		found[token] = decodeURIComponent(pathMatch[index + 1]);
	});

	const current = new URLSearchParams(window.location.search);
	let matched = true;
	template.searchParams.forEach((templateValue, key) => {
		if (!matched) {
			return;
		}
		const currentValue = current.get(key);
		if (currentValue === null) {
			matched = false;
			return;
		}
		const value = reelsMatcher(templateValue);
		const valueMatch = value.expression.exec(currentValue);
		if (!valueMatch) {
			matched = false;
			return;
		}
		value.tokens.forEach((token, index) => {
			found[token] = decodeURIComponent(valueMatch[index + 1]);
		});
	});

	return matched && (found.mediaId || found.playlistId) ? found : null;
};

/** The same address with this clip in it, leaving every other part of the URL alone. */
export const writeReelsUrl = (pattern, values) => {
	const template = reelsTemplate(pattern);
	const params = new URLSearchParams(window.location.search);
	template.searchParams.forEach((templateValue, key) => params.set(key, fillReelsTokens(templateValue, values)));
	// URLSearchParams escapes a slash inside a value; %2F in a link is one nobody believes is theirs.
	const search = params.toString().replace(/%2F/gi, '/');
	return fillReelsTokens(template.pathname, values) + (search ? `?${search}` : '') + window.location.hash;
};

/**
 * The declared pattern as an absolute share template, tokens intact - so one declaration serves both
 * the address bar and sharing. Absolute, because a shared link has to work away from this page.
 */
export const reelsShareTemplate = (pattern) => {
	if (!pattern) {
		return '';
	}
	return fillReelsTokens(reelsTemplate(pattern).href, { mediaId: '{mediaId}', playlistId: '{playlistId}' });
};

/** Where closing goes when there is no history to go back to - a link opened cold. */
export const reelsGalleryUrl = (pattern, override) => {
	if (override) {
		return override;
	}
	const template = reelsTemplate(pattern);
	if (template.pathname === window.location.pathname) {
		// The clip is in the query: this same page, without the parameters the pattern owns.
		const params = new URLSearchParams(window.location.search);
		template.searchParams.forEach((templateValue, key) => params.delete(key));
		const search = params.toString();
		return window.location.pathname + (search ? `?${search}` : '');
	}
	// The clip is in the path, so there is no gallery to recover; a host that knows passes galleryUrl.
	return '/';
};
